import React, { createContext, useContext, useState, useEffect } from 'react'

const CartContext = createContext(null)

const CART_STORAGE_KEY = 'overgaard_fest_cart_v1'

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem(CART_STORAGE_KEY)
      if (saved) return JSON.parse(saved)
    } catch (e) {
      console.warn('Could not load cart from localStorage', e)
    }
    return {
      menu: null, // { starters, meats, potatoes, salads, pricePerGuest, totalPrice, guestCount }
      rentals: [], // array of { id, name, price, quantity, options, image, note }
    }
  })

  const [isCartOpen, setIsCartOpen] = useState(false)

  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart))
    } catch (e) {
      console.warn('Could not save cart to localStorage', e)
    }
  }, [cart])

  // Menu methods
  const setMenuInCart = (menuData) => {
    setCart((prev) => ({
      ...prev,
      menu: menuData,
    }))
    setIsCartOpen(true)
  }

  const removeMenuFromCart = () => {
    setCart((prev) => ({
      ...prev,
      menu: null,
    }))
  }

  // Rental methods
  const addRentalToCart = (rentalItem) => {
    setCart((prev) => {
      const existingIdx = prev.rentals.findIndex((r) => r.id === rentalItem.id)
      let updatedRentals = [...prev.rentals]
      if (existingIdx >= 0) {
        // Replace with updated options/quantity or merge
        updatedRentals[existingIdx] = {
          ...updatedRentals[existingIdx],
          ...rentalItem,
        }
      } else {
        updatedRentals.push(rentalItem)
      }
      return {
        ...prev,
        rentals: updatedRentals,
      }
    })
    setIsCartOpen(true)
  }

  const removeRentalFromCart = (rentalId) => {
    setCart((prev) => ({
      ...prev,
      rentals: prev.rentals.filter((r) => r.id !== rentalId),
    }))
  }

  const updateRentalQuantity = (rentalId, delta) => {
    setCart((prev) => ({
      ...prev,
      rentals: prev.rentals
        .map((r) => {
          if (r.id === rentalId) {
            const newQty = Math.max(0, (r.quantity || 1) + delta)
            return { ...r, quantity: newQty }
          }
          return r
        })
        .filter((r) => (r.quantity || 1) > 0),
    }))
  }

  const clearCart = () => {
    setCart({
      menu: null,
      rentals: [],
    })
  }

  // Counts and totals
  const totalItemCount = (cart.menu ? 1 : 0) + cart.rentals.reduce((sum, r) => sum + (r.quantity || 1), 0)

  const menuTotal = cart.menu?.totalPrice || 0
  const rentalsTotal = cart.rentals.reduce((sum, r) => {
    const unitPrice = Number(r.price) || 0
    const qty = Number(r.quantity) || 1
    const extrasPrice = Number(r.extrasCost) || 0
    return sum + (unitPrice * qty) + extrasPrice
  }, 0)

  const grandTotal = menuTotal + rentalsTotal

  return (
    <CartContext.Provider
      value={{
        cart,
        isCartOpen,
        setIsCartOpen,
        openCart: () => setIsCartOpen(true),
        closeCart: () => setIsCartOpen(false),
        setMenuInCart,
        removeMenuFromCart,
        addRentalToCart,
        removeRentalFromCart,
        updateRentalQuantity,
        clearCart,
        totalItemCount,
        menuTotal,
        rentalsTotal,
        grandTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

export function useCart() {
  const ctx = useContext(CartContext)
  if (!ctx) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return ctx
}
